/**
 * cart-full.js — client script for the custom /cart page section.
 *
 * Responsibilities:
 *   1. Quantity steppers for native Shopify cart lines using RSV min/step rules.
 *   2. Summary, cart count, and free-shipping bar updates after Ajax changes.
 *   3. Checkout CTA routing through window.pbCheckout() when a project is present.
 */
(function () {
  'use strict';

  const SYNC_DELAY = 180;
  const desiredQuantities = new Map();
  let syncTimer;
  let syncInFlight = false;
  let inFlightKeys = new Set();

  function money(cents) {
    return (Number(cents || 0) / 100).toLocaleString('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  }

  function getCfg() {
    const meta = document.querySelector('meta[name="cart-full-config"]');
    return {
      threshold: Number(meta?.dataset.threshold || 250000),
      subtotal: Number(meta?.dataset.subtotal || 0),
      count: Number(meta?.dataset.count || 0),
      flatSqft: Number(meta?.dataset.flatSqft || 0),
      cornerLnft: Number(meta?.dataset.cornerLnft || 0),
      accessoryPieces: Number(meta?.dataset.accessoryPieces || 0),
    };
  }

  function persistCfg(cfg) {
    const meta = document.querySelector('meta[name="cart-full-config"]');
    if (!meta) return;

    meta.dataset.subtotal = String(cfg.subtotal);
    meta.dataset.count = String(cfg.count);
    meta.dataset.flatSqft = String(cfg.flatSqft);
    meta.dataset.cornerLnft = String(cfg.cornerLnft);
    meta.dataset.accessoryPieces = String(cfg.accessoryPieces);
  }

  function updateShipBar(cfg) {
    const fill = document.querySelector('[data-cart-full-ship-fill]');
    const remaining = document.querySelector('[data-cart-full-ship-remaining]');
    const pctEl = document.querySelector('[data-cart-full-ship-pct]');
    if (!fill) return;

    const pct = cfg.threshold > 0 ? Math.min(100, Math.round((cfg.subtotal / cfg.threshold) * 100)) : 0;
    fill.style.width = `${pct}%`;
    if (pctEl) pctEl.textContent = `${pct}%`;
    if (remaining) {
      remaining.textContent = cfg.subtotal >= cfg.threshold
        ? 'Free shipping unlocked for this qualifying order'
        : `${money(cfg.threshold - cfg.subtotal)} away from ${money(cfg.threshold)} free shipping`;
    }
  }

  function updateCartBadges(count) {
    document.querySelectorAll('[data-cart-full-count], [data-cart-full-products], [data-cart-full-summary-products]').forEach((el) => {
      el.textContent = String(count);
    });
  }

  function classifyItem(item) {
    const words = [
      item.product_title,
      item.handle,
      item.product_type,
      item.variant_title,
    ].filter(Boolean).join(' ').toLowerCase();

    if (words.includes('corner')) return 'corners';
    if (
      words.includes('sample') ||
      words.includes('accessor') ||
      words.includes('window sill') ||
      words.includes('water table') ||
      words.includes('capstone') ||
      words.includes('trimstone') ||
      words.includes('light block') ||
      words.includes('electrical box') ||
      words.includes('faucet') ||
      words.includes('hose bib') ||
      words.includes('hosebib') ||
      words.includes('outlet') ||
      words.includes('wainscot') ||
      words.includes('sill')
    ) return 'accessories';

    return 'flats';
  }

  function projectMetrics(cart) {
    return (cart.items || []).reduce((metrics, item) => {
      const group = classifyItem(item);
      const quantity = Number(item.quantity || 0);
      if (group === 'corners') metrics.cornerLnft += quantity;
      else if (group === 'accessories') metrics.accessoryPieces += quantity;
      else metrics.flatSqft += quantity;
      return metrics;
    }, {
      count: (cart.items || []).length,
      flatSqft: 0,
      cornerLnft: 0,
      accessoryPieces: 0,
    });
  }

  function setText(selector, value) {
    document.querySelectorAll(selector).forEach((el) => {
      el.textContent = String(value);
    });
  }

  function updateProjectSummary(cfg) {
    setText('[data-cart-full-flats], [data-cart-full-summary-flats]', cfg.flatSqft);
    setText('[data-cart-full-corners], [data-cart-full-summary-corners]', cfg.cornerLnft);
    setText('[data-cart-full-accessories], [data-cart-full-summary-accessories], [data-cart-full-completeness-accessories]', cfg.accessoryPieces);
  }

  function updateSummary(cfg) {
    const sub = document.querySelector('[data-cart-full-subtotal]');
    const tot = document.querySelector('[data-cart-full-total]');
    const mobileTotal = document.querySelector('[data-cart-full-mobile-total]');
    const shipping = document.querySelector('[data-cart-full-shipping]');

    if (sub) sub.textContent = money(cfg.subtotal);
    if (tot) tot.textContent = money(cfg.subtotal);
    if (mobileTotal) mobileTotal.textContent = money(cfg.subtotal);
    if (shipping) {
      shipping.innerHTML = cfg.subtotal >= cfg.threshold
        ? '<span class="cart-full__ship-free">Free for qualifying order</span>'
        : 'Confirmed after address';
    }

    updateCartBadges(cfg.count);
    updateProjectSummary(cfg);
  }

  function numberFrom(value, fallback) {
    const parsed = Number.parseInt(value, 10);
    return Number.isFinite(parsed) ? parsed : fallback;
  }

  function getRowConfig(row) {
    return {
      key: row.dataset.lineKey || '',
      variantId: row.dataset.variantId || '',
      quantity: numberFrom(row.dataset.cartFullQuantity, 1),
      min: Math.max(1, numberFrom(row.dataset.cartFullMin, 1)),
      step: Math.max(1, numberFrom(row.dataset.cartFullStep, 1)),
      unitPrice: Math.max(0, numberFrom(row.dataset.cartFullUnitPrice, 0)),
      unit: row.dataset.cartFullUnit || 'pc',
      rule: row.dataset.cartFullRule || 'normal',
      group: row.dataset.cartFullGroup || 'flats',
    };
  }

  function normalizeQuantity(quantity, cfg) {
    const current = Math.max(0, numberFrom(quantity, cfg.min));
    if (current < cfg.min) return cfg.min;

    const offset = current - cfg.min;
    const remainder = offset % cfg.step;
    return remainder === 0 ? current : current + (cfg.step - remainder);
  }

  function getNextQuantity(row, direction) {
    const cfg = getRowConfig(row);
    const input = row.querySelector('[data-cart-full-qty-input]');
    const current = numberFrom(input?.value, cfg.quantity);
    const normalized = normalizeQuantity(current, cfg);

    if (direction === 'plus') {
      return current === normalized ? current + cfg.step : normalized;
    }

    return Math.max(cfg.min, normalized - cfg.step);
  }

  function updateRowSyncState(row) {
    const keys = [
      row.dataset.lineKey || '',
      row.dataset.variantId || '',
    ].filter(Boolean);
    row.classList.toggle(
      'cart-full-row--updating',
      keys.some((key) => desiredQuantities.has(key) || inFlightKeys.has(key)),
    );
  }

  function updateRowControls(row, quantity) {
    const cfg = getRowConfig(row);
    const input = row.querySelector('[data-cart-full-qty-input]');
    const minus = row.querySelector('[data-cart-full-qty="minus"]');
    row.dataset.cartFullQuantity = String(quantity);
    if (input) input.value = String(quantity);
    if (minus) minus.disabled = quantity <= cfg.min;
  }

  function updateRowFromItem(row, item) {
    const price = row.querySelector('[data-cart-full-line-price]');
    const nextQuantity = Number(item.quantity || 0);

    row.dataset.lineKey = item.key || row.dataset.lineKey || '';
    row.dataset.variantId = String(item.variant_id || item.id || row.dataset.variantId || '');
    row.dataset.cartFullUnitPrice = String(item.final_price || row.dataset.cartFullUnitPrice || 0);
    updateRowControls(row, nextQuantity);
    if (price) price.textContent = money(item.final_line_price);
  }

  function findCartItem(cart, cfg) {
    return (cart.items || []).find((item) => {
      return item.key === cfg.key || String(item.variant_id || item.id) === cfg.variantId;
    });
  }

  function applyCartState(cart) {
    const cfg = getCfg();
    const metrics = projectMetrics(cart);
    cfg.subtotal = cart.total_price;
    cfg.count = metrics.count;
    cfg.flatSqft = metrics.flatSqft;
    cfg.cornerLnft = metrics.cornerLnft;
    cfg.accessoryPieces = metrics.accessoryPieces;
    persistCfg(cfg);
    updateSummary(cfg);
    updateShipBar(cfg);

    document.querySelectorAll('[data-cart-full-row]').forEach((row) => {
      const rowCfg = getRowConfig(row);
      const item = findCartItem(cart, rowCfg);
      if (item) updateRowFromItem(row, item);
      else row.remove();
    });

    if (cart.item_count === 0) window.location.reload();
  }

  function applyOptimisticQuantity(row, quantity) {
    const cfg = getRowConfig(row);
    const nextQuantity = normalizeQuantity(quantity, cfg);
    const delta = nextQuantity - cfg.quantity;
    if (delta === 0) {
      updateRowControls(row, nextQuantity);
      return nextQuantity;
    }

    updateRowControls(row, nextQuantity);
    const price = row.querySelector('[data-cart-full-line-price]');
    if (price) price.textContent = money(cfg.unitPrice * nextQuantity);

    const cartCfg = getCfg();
    cartCfg.subtotal += cfg.unitPrice * delta;
    if (cfg.group === 'corners') cartCfg.cornerLnft += delta;
    else if (cfg.group === 'accessories') cartCfg.accessoryPieces += delta;
    else cartCfg.flatSqft += delta;
    persistCfg(cartCfg);
    updateSummary(cartCfg);
    updateShipBar(cartCfg);

    return nextQuantity;
  }

  function rowForKey(key) {
    return Array.from(document.querySelectorAll('[data-cart-full-row]')).find((row) => {
      return row.dataset.lineKey === key || row.dataset.variantId === key;
    });
  }

  function reapplyDesiredQuantities() {
    desiredQuantities.forEach((quantity, key) => {
      const row = rowForKey(key);
      if (!row) {
        desiredQuantities.delete(key);
        return;
      }
      applyOptimisticQuantity(row, quantity);
      updateRowSyncState(row);
    });
  }

  function scheduleSync() {
    window.clearTimeout(syncTimer);
    syncTimer = window.setTimeout(flushQuantityChanges, SYNC_DELAY);
  }

  async function refreshCartState() {
    const response = await fetch('/cart.js', {
      headers: { Accept: 'application/json' },
    });
    if (!response.ok) throw new Error(`Cart refresh failed: ${response.status}`);
    return response.json();
  }

  async function flushQuantityChanges() {
    if (syncInFlight || desiredQuantities.size === 0) return;

    const sentQuantities = new Map(desiredQuantities);
    const updates = Object.fromEntries(sentQuantities);
    syncInFlight = true;
    inFlightKeys = new Set(sentQuantities.keys());
    inFlightKeys.forEach((key) => {
      const row = rowForKey(key);
      if (row) updateRowSyncState(row);
    });

    try {
      const response = await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ updates }),
        keepalive: true,
      });

      if (!response.ok) throw new Error(`Cart update failed: ${response.status}`);

      const cart = await response.json();
      sentQuantities.forEach((quantity, key) => {
        if (desiredQuantities.get(key) === quantity) desiredQuantities.delete(key);
      });
      applyCartState(cart);
      reapplyDesiredQuantities();
    } catch (error) {
      console.warn('[cart-full] quantity update failed', error);
      try {
        const cart = await refreshCartState();
        sentQuantities.forEach((quantity, key) => {
          if (desiredQuantities.get(key) === quantity) desiredQuantities.delete(key);
        });
        applyCartState(cart);
        reapplyDesiredQuantities();
      } catch (refreshError) {
        console.warn('[cart-full] cart refresh failed', refreshError);
        window.location.reload();
      }
    } finally {
      syncInFlight = false;
      inFlightKeys = new Set();
      document.querySelectorAll('[data-cart-full-row]').forEach(updateRowSyncState);
      if (desiredQuantities.size > 0) void flushQuantityChanges();
    }
  }

  async function waitForCartSync() {
    window.clearTimeout(syncTimer);

    while (desiredQuantities.size > 0 || syncInFlight) {
      if (!syncInFlight && desiredQuantities.size > 0) {
        await flushQuantityChanges();
      } else {
        await new Promise((resolve) => window.setTimeout(resolve, 25));
      }
    }
  }

  function changeLineQuantity(row, quantity) {
    const cfg = getRowConfig(row);
    const syncKey = cfg.variantId || cfg.key;
    if (!syncKey) return;

    const nextQuantity = applyOptimisticQuantity(row, quantity);
    if (nextQuantity === cfg.quantity) return;

    desiredQuantities.set(syncKey, nextQuantity);
    updateRowSyncState(row);
    scheduleSync();

    window.pbLog?.track?.('pb_cart_page_quantity_changed', {
      variant_id: cfg.variantId,
      rule: cfg.rule,
      unit: cfg.unit,
      quantity: nextQuantity,
    });
  }

  function wireQuantityControls() {
    document.querySelectorAll('[data-cart-full-row]').forEach((row) => {
      updateRowControls(row, getRowConfig(row).quantity);
    });

    document.addEventListener('click', (event) => {
      const button = event.target.closest('[data-cart-full-qty]');
      if (!button) return;

      const row = button.closest('[data-cart-full-row]');
      if (!row) return;

      event.preventDefault();
      event.stopPropagation();
      changeLineQuantity(row, getNextQuantity(row, button.dataset.cartFullQty));
    });

    document.addEventListener('change', (event) => {
      const input = event.target.closest('[data-cart-full-qty-input]');
      if (!input) return;

      const row = input.closest('[data-cart-full-row]');
      if (!row) return;

      changeLineQuantity(row, input.value);
    });

    document.addEventListener('keydown', (event) => {
      const input = event.target.closest('[data-cart-full-qty-input]');
      if (!input || event.key !== 'Enter') return;

      event.preventDefault();
      input.blur();
      input.dispatchEvent(new Event('change', { bubbles: true }));
    });
  }

  function wireCheckout() {
    const buttons = document.querySelectorAll('[data-cart-full-checkout]');
    if (!buttons.length) return;

    buttons.forEach((btn) => {
      btn.addEventListener('click', async (event) => {
        const project = window.pbStore?.activeProject?.();
        const hasProject = Boolean(project?.items?.length);
        const hasPendingCartSync = desiredQuantities.size > 0 || syncInFlight;
        if (!hasProject && !hasPendingCartSync) return;

        event.preventDefault();
        buttons.forEach((button) => {
          button.dataset.cartFullOriginalHtml = button.innerHTML;
          button.disabled = true;
          button.setAttribute('aria-busy', 'true');
          button.classList.add('cart-full__checkout--loading');
          const label = button.querySelector('[data-cart-full-checkout-label]');
          if (label) label.textContent = 'Saving cart…';
        });

        try {
          await waitForCartSync();

          if (hasProject) {
            window.pbLog?.track?.('pb_cart_page_checkout', { project_id: project.id });
            if (window.pbCheckout) {
              window.pbCheckout({ navigate: true });
              return;
            }
          }

          btn.closest('form')?.submit();
        } catch (error) {
          console.warn('[cart-full] checkout sync failed', error);
          buttons.forEach((button) => {
            button.disabled = false;
            button.removeAttribute('aria-busy');
            button.classList.remove('cart-full__checkout--loading');
            if (button.dataset.cartFullOriginalHtml) {
              button.innerHTML = button.dataset.cartFullOriginalHtml;
            }
          });
        }
      });
    });
  }

  function init() {
    const cfg = getCfg();
    updateShipBar(cfg);
    wireQuantityControls();
    wireCheckout();
    window.pbLog?.track?.('pb_cart_page_viewed', { count: cfg.count, subtotal: cfg.subtotal });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
